import { useInfiniteQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { Controls, ControlsButton } from '@/components/Controls';
import ExportButton from '@/components/ExportButton';
import InfiniteLoader from '@/components/InfiniteLoader';
import InfoTooltip from '@/components/InfoTooltip';
import { SectionHeading } from '@/components/Layout/Parts';
import SearchBar from '@/components/SearchBar';
import {
  ResponsiveTable,
  Table,
  TableContent,
  TableHeader,
  Th,
} from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import useHasPermission from '@/hooks/useHasPermission';
import {
  FreelancerHolidayRequestResource,
  PaginatedResponse,
} from '@/types/resources';
import { api } from '@/utils/api';
import { VacationStatus } from '@/utils/enums';
import { RootState } from '@/utils/store';

import VacationApprovalTableRow from './VacationApprovalTableRow';
import VacationApprovalsFilter from './VacationApprovalsFilter';

const tabs = [
  { value: VacationStatus.Pending, title: 'Pending' },
  { value: VacationStatus.Approved, title: 'Approved' },
  { value: VacationStatus.Declined, title: 'Declined' },
  { value: VacationStatus.Auto_Declined, title: 'Auto Declined' },
];

const StyledTable = styled(Table)`
  th {
    font-size: 11px;
  }
  th,
  td {
    padding: 24px 16px;
    font-size: 12px;
    font-weight: 700;
    &:nth-child(1) {
      padding-left: 24px;
      width: 25%;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
`;

const StyledTableHeader = styled(TableHeader)`
  position: relative;
`;

const ButtonsHolder = styled.div`
  display: flex;
  margin-left: auto;
  gap: 8px;
`;

const VacationApprovalTable = () => {
  const [activeStatus, setActiveStatus] = useState(tabs[0]);
  const [search, setSearch] = useState<string>('');
  const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>({});

  const currentUserId = useSelector(
    (rootState: RootState) => rootState.auth.user!.id,
  );

  const isHrUser = useHasPermission('manage freelancers');
  const canManageRequests = useHasPermission(
    'manage freelancers',
    'view companies vacations',
    'view departments vacations',
  );
  const debouncedSearch = useDebounce(search, 300);

  const params = {
    ...(isHrUser
      ? {
          'filter[hr_status]': activeStatus.value,
        }
      : {
          'filter[team_lead_status]': activeStatus.value,
          'filter[team_lead_id]': currentUserId,
        }),
    ...(!!search && {
      'filter[search]': search,
    }),
    ...(selectedDepartments.length > 0 && {
      'filter[department_id]': selectedDepartments,
    }),
    ...(selectedCompanies.length > 0 && {
      'filter[company_id]': selectedCompanies,
    }),
    ...(selectedDateRange.rangeStart && {
      'filter[date_from]': selectedDateRange.rangeStart.toFormat('yyyy-MM-dd'),
      'filter[date_to]': (
        selectedDateRange.rangeEnd ?? selectedDateRange.rangeStart
      ).toFormat('yyyy-MM-dd'),
    }),
  };

  const fetchRequests = ({
    pageParam = '/v1/payments/freelancers/holidays/requests',
  }) => {
    return api.get<never, PaginatedResponse<FreelancerHolidayRequestResource>>(
      pageParam,
      { params },
    );
  };

  const fetchCsv = () =>
    api.get<never, string>(
      '/v1/payments/freelancers/holidays/requests-export',
      { params },
    );

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<FreelancerHolidayRequestResource>>(
    [
      'freelancer_vacation_requests',
      {
        status: activeStatus.value,
        ...(!!debouncedSearch && {
          search: debouncedSearch,
        }),
        ...(selectedDepartments.length > 0 && {
          department_id: selectedDepartments,
        }),
        ...(selectedCompanies.length > 0 && {
          company_id: selectedCompanies,
        }),
        ...(selectedDateRange.rangeStart && {
          date_from: selectedDateRange.rangeStart.toFormat('yyyy-MM-dd'),
          date_to: (
            selectedDateRange.rangeEnd ?? selectedDateRange.rangeStart
          ).toFormat('yyyy-MM-dd'),
        }),
      },
    ],
    fetchRequests,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  const requests = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<FreelancerHolidayRequestResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  return (
    <>
      <StyledTableHeader>
        <Controls>
          {tabs.map((tab, index) => (
            <ControlsButton
              key={index}
              active={tab.value === activeStatus.value}
              onClick={() => setActiveStatus(tab)}
            >
              {tab.title}
            </ControlsButton>
          ))}
        </Controls>
        <ButtonsHolder>
          <ExportButton
            fetchCsv={fetchCsv}
            fileName="vacation-requests-export.csv"
          />
          <VacationApprovalsFilter
            selectedDepartments={selectedDepartments}
            setSelectedDepartments={setSelectedDepartments}
            selectedCompanies={selectedCompanies}
            setSelectedCompanies={setSelectedCompanies}
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
          />
        </ButtonsHolder>
      </StyledTableHeader>
      <SearchBar
        placeholder="Search by Freelancer Name"
        value={search}
        setValue={setSearch}
      />

      <SectionHeading>{activeStatus.title} approvals</SectionHeading>
      <ResponsiveTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>Name</Th>
              {activeStatus.value === VacationStatus.Pending ? (
                <>
                  <Th>Request Date</Th>
                  <Th>Vacation Date</Th>
                  <Th>Deducted Days</Th>
                </>
              ) : (
                <>
                  <Th>Vacation Date</Th>
                  <Th>Deducted Days</Th>
                </>
              )}
              <Th>
                Type
                <InfoTooltip
                  title="Type"
                  content="Paid leave uses your available balance. Unpaid leave is uncompensated"
                />
              </Th>
              <Th>Available Days</Th>
              <Th>Team Lead Status</Th>
              <Th>HR Status</Th>
              {canManageRequests && <Th $actionsCol></Th>}
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={9}
              isError={isError}
              hasData={!!requests && requests.length > 0}
            >
              {requests?.map((request) => (
                <VacationApprovalTableRow
                  request={request}
                  key={request.id}
                  activeStatus={activeStatus}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default VacationApprovalTable;
